import { useEffect } from 'react';
import { useInvoices } from '../../hooks/useInvoices';
import BrainiacsLoader from '../atoms/BrainiacsLoader';

interface Invoice {
  id: string;
  number: string;
  status: string;
  amount_due: number;
  amount_paid: number;
  currency: string;
  created: number;
  due_date?: number;
  invoice_pdf?: string;
  hosted_invoice_url?: string;
  customer_email?: string;
  description?: string;
}

interface InvoicesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function InvoicesModal({ isOpen, onClose }: InvoicesModalProps) {
  // Use TanStack Query hook instead of manual state management
  const { data: result, isLoading: loading, error: queryError, refetch } = useInvoices();

  // Refetch when modal opens
  useEffect(() => {
    if (isOpen) {
      console.log('📄 Fetching invoices...');
      refetch();
    }
  }, [isOpen, refetch]);

  // Transform query data
  const invoices = result?.success && result?.data
    ? (Array.isArray(result.data) ? result.data : [result.data])
    : [];

  const error = queryError ? (queryError as Error).message : (result?.error || null);

  // Log successful load
  useEffect(() => {
    if (!loading && result?.success) {
      console.log('✅ Invoices loaded from cache or API:', invoices);
    }
  }, [loading, result, invoices]);

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'paid':
        return 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20';
      case 'open':
        return 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20';
      case 'draft':
        return 'text-gray-400 bg-gray-500/10 border-gray-500/20';
      case 'void':
        return 'text-red-400 bg-red-500/10 border-red-500/20';
      case 'uncollectible':
        return 'text-orange-400 bg-orange-500/10 border-orange-500/20';
      default:
        return 'text-gray-400 bg-gray-500/10 border-gray-500/20';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="relative w-full max-w-4xl bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] border border-[#5260FF]/30 rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="relative p-6 border-b border-white/5">
          <div className="absolute inset-0 bg-gradient-to-r from-[#5260FF]/5 to-transparent pointer-events-none"></div>

          <div className="relative flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-white mb-1">Your Invoices</h2>
              <p className="text-sm text-gray-400">View and download your Stripe invoices</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/5 rounded-xl transition-all"
              aria-label="Close"
            >
              <svg className="w-6 h-6 text-gray-400 hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <BrainiacsLoader variant="inline" message="Loading invoices..." />
            </div>
          )}

          {error && (
            <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {!loading && !error && invoices.length === 0 && (
            <div className="text-center py-12">
              <svg className="w-16 h-16 mx-auto text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-gray-400 text-lg mb-2">No invoices found</p>
              <p className="text-gray-500 text-sm">Your invoices will appear here once you make a purchase</p>
            </div>
          )}

          {!loading && !error && invoices.length > 0 && (
            <div className="space-y-3">
              {invoices.map((invoice) => (
                <div
                  key={invoice.id}
                  className="p-4 bg-white/[0.02] border border-white/5 rounded-xl hover:bg-white/[0.04] hover:border-white/10 transition-all"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-white font-semibold truncate">
                          {invoice.number || `Invoice #${invoice.id.slice(-8)}`}
                        </span>
                        <span className={`px-2.5 py-1 rounded-lg text-xs font-semibold uppercase border ${getStatusColor(invoice.status)}`}>
                          {invoice.status}
                        </span>
                      </div>

                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400">
                        <span>📅 {formatDate(invoice.created)}</span>
                        {invoice.due_date && (
                          <span>⏰ Due: {formatDate(invoice.due_date)}</span>
                        )}
                        {invoice.customer_email && (
                          <span className="truncate">📧 {invoice.customer_email}</span>
                        )}
                      </div>

                      {invoice.description && (
                        <p className="text-sm text-gray-500 mt-2 line-clamp-1">{invoice.description}</p>
                      )}
                    </div>

                    <div className="flex flex-col items-end gap-2 flex-shrink-0">
                      <div className="text-right">
                        <div className="text-xl font-bold text-white">
                          {formatAmount(invoice.amount_due, invoice.currency)}
                        </div>
                        {invoice.amount_paid > 0 && invoice.amount_paid !== invoice.amount_due && (
                          <div className="text-xs text-emerald-400">
                            Paid: {formatAmount(invoice.amount_paid, invoice.currency)}
                          </div>
                        )}
                      </div>

                      <div className="flex gap-2">
                        {invoice.hosted_invoice_url && (
                          <a
                            href={invoice.hosted_invoice_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3 py-1.5 bg-[#5260FF]/10 hover:bg-[#5260FF]/20 border border-[#5260FF]/20 hover:border-[#5260FF]/40 text-[#5260FF] rounded-lg text-xs font-semibold transition-all"
                          >
                            View
                          </a>
                        )}
                        {invoice.invoice_pdf && (
                          <a
                            href={invoice.invoice_pdf}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 text-white rounded-lg text-xs font-semibold transition-all"
                          >
                            PDF
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-white/5 bg-white/[0.01]">
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500">
              Showing {invoices.length} invoice{invoices.length !== 1 ? 's' : ''}
            </p>
            <button
              onClick={onClose}
              className="px-6 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 text-white rounded-xl font-semibold transition-all"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
